import {toast} from '@Toast';
import {useState} from 'react';

type ImageLinkPreviewProps = {
  href: string;
};

const getImageFilename = (href: string): string => {
  try {
    const pathname = decodeURIComponent(new URL(href).pathname);
    const filename = pathname.split('/').filter(Boolean).at(-1);

    return filename || `SnappImage_${Date.now()}`;
  } catch {
    return `SnappImage_${Date.now()}`;
  }
};

const triggerDownload = (href: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const ImageLinkPreview: React.FC<ImageLinkPreviewProps> = ({href}) => {
  const [hasError, setHasError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (hasError) {
    return null;
  }

  const handleDownload = async () => {
    if (downloading) {
      return;
    }

    setDownloading(true);

    try {
      const response = await fetch(href);

      if (!response.ok) {
        throw new Error(`Image download failed with status ${response.status}`);
      }

      const blobUrl = window.URL.createObjectURL(await response.blob());
      triggerDownload(blobUrl, getImageFilename(href));
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast('دانلود تصویر با خطا مواجه شد');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      aria-label="دانلود تصویر"
      aria-busy={downloading}
      className="relative mt-2 block size-20 overflow-hidden rounded-lg border border-gray-03 bg-gray-02"
      onClick={handleDownload}
    >
      <img
        src={href}
        alt="پیش‌نمایش تصویر"
        className="size-full object-cover"
        loading="lazy"
        onError={() => setHasError(true)}
      />
      {downloading ? (
        <span
          className="absolute inset-0 flex items-center justify-center bg-black/35 text-body-xs text-white"
          aria-hidden
        >
          ...
        </span>
      ) : null}
    </button>
  );
};

export default ImageLinkPreview;
